import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Header, Footer } from "@/components/landing/layout";
import { ScrollToTop, CookieBanner } from "@/components/landing/ui";
import { BRLDPattern, BRLDPatternMinimal } from "@/components/landing/ui/BRLDPattern";
import {
  IconArrowRight,
  IconCheck,
  IconShield,
  IconLock,
  IconEye,
  IconBolt,
  IconClock,
  IconFileText,
  IconTrendingUp,
  IconWallet,
  IconCopy,
  IconCircleCheck,
  IconExternalLink,
  IconAlertCircle,
  IconCoin,
  IconPlus,
  IconMinus,
} from "@tabler/icons-react";

// Animações mais sutis e profissionais (menos "Framer footprint")
const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05
    }
  }
};

// Componente para números com formatação especial (1B+, 7M+, etc)
function AnimatedNumber({ 
  value, 
  suffix = "", 
  prefix = "", 
  duration = 2,
  format = "number" // "number" | "billion" | "million" | "plain"
}: { 
  value: number; 
  suffix?: string; 
  prefix?: string;
  duration?: number;
  format?: "number" | "billion" | "million" | "plain";
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const countRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const startAnimation = () => {
      startTimeRef.current = Date.now();
      
      const animate = () => {
        if (!startTimeRef.current) return;
        
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        countRef.current = value * easeOut;
        
        setDisplayValue(Math.round(countRef.current));
        
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
        }
      };
      
      animate();
    };
    
    startAnimation();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, duration]);

  const formatNumber = (num: number): string => {
    if (format === "billion") {
      return (num / 1000000000).toFixed(0) + "B";
    } else if (format === "million") {
      return (num / 1000000).toFixed(0) + "M";
    } else if (format === "plain") {
      return num.toString();
    }
    // Auto format
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(0) + "B";
    } else if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + "K";
    }
    return num.toString();
  };

  return (
    <span>
      {prefix}
      {formatNumber(displayValue)}
      {suffix}
    </span>
  );
}

// Hero Section - Design minimalista e institucional
function HeroSection() {
  return (
    <section
      className="relative overflow-hidden bg-white pt-24 sm:pt-32 lg:pt-36 pb-12 sm:pb-16"
      aria-labelledby="hero-heading"
    >
      {/* Background Pattern */}
      <BRLDPattern variant="subtle" opacity={0.03} size={24} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              id="hero-heading"
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 mb-4 sm:mb-6 leading-[1.15]"
              variants={fadeInUp}
            >
              BRLD: o real programável para liquidação institucional e tesouraria corporativa
            </motion.h1>

            <motion.div
              className="text-sm sm:text-base lg:text-lg text-slate-600 mb-6 sm:mb-10 max-w-2xl leading-relaxed"
              variants={fadeInUp}
            >
              <p>
                A BRLD é a stablecoin da Liqi pareada 1:1 ao real brasileiro, criada como infraestrutura institucional de liquidação para operações financeiras tokenizadas e como instrumento de modernização da tesouraria corporativa.
              </p>
            </motion.div>

            {/* CTA Buttons - Design mais limpo */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8"
              variants={fadeInUp}
            >
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-lg px-6 sm:px-8 py-3 sm:py-3.5 text-sm font-medium text-white transition-all duration-200 hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900"
                style={{ backgroundColor: '#0038B5' }}
              >
                Acessar a Plataforma BRLD
              </a>
              <a
                href="#pilares"
                className="inline-flex items-center justify-center rounded-lg border-2 border-[#0038B5] bg-transparent px-6 sm:px-8 py-3 sm:py-3.5 text-sm font-medium text-[#0038B5] hover:bg-[#0038B5]/5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0038B5]"
              >
                Conferir casos de uso
                <IconArrowRight className="ml-2 w-4 h-4 gradient-icon" strokeWidth={2} />
              </a>
            </motion.div>

          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            className="flex items-center justify-center lg:justify-end order-first lg:order-last"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative w-full max-w-md lg:max-w-2xl">
              <img
                src="/images/shutterstock_2021639264.jpg"
                alt="BRLD - Real programável da Liqi"
                className="rounded-xl shadow-lg w-full h-auto"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Carrossel de Features Section
function FeaturesCarouselSection() {
  const features = [
    {
      icon: IconCheck,
      title: "1 BRLD = R$ 1",
      description: "Paridade garantida"
    },
    {
      icon: IconShield,
      title: "Lastro integral",
      description: "Tesouro Nacional"
    },
    {
      icon: IconLock,
      title: "Arquitetura segregada",
      description: "Funções separadas e independentes"
    },
    {
      icon: IconEye,
      title: "Transparência",
      description: "Reservas e tokens verificáveis"
    }
  ];

  // Duplica os cards para criar loop infinito
  const duplicatedFeatures = [...features, ...features];

  return (
    <section className="relative bg-white pb-8 sm:pb-16 overflow-hidden" aria-labelledby="features-carousel-heading">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Mobile: Grid estático / Desktop: Carrossel */}
        <div className="hidden sm:block relative overflow-hidden">
          <motion.div
            className="flex gap-4"
            animate={{
              x: [0, `-${(features.length * 100) / duplicatedFeatures.length}%`]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            {duplicatedFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="flex-shrink-0"
                  style={{ width: `${100 / features.length}%` }}
                >
                  <div className="bg-slate-50/50 rounded-lg p-5 border border-slate-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0038B5]/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 gradient-icon" strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-base font-semibold text-slate-900 mb-0.5 truncate">{feature.title}</p>
                        <p className="text-xs text-slate-600 leading-relaxed truncate">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Mobile: Grid 2x2 estático */}
        <div className="grid grid-cols-2 gap-3 sm:hidden">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={index}
                className="bg-slate-50/50 rounded-lg p-4 border border-slate-100"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-[#0038B5]/10 flex items-center justify-center">
                    <IconComponent className="w-5 h-5 gradient-icon" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-0.5">{feature.title}</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Sócios Section
function SociosSection() {
  const socios = [
    {
      name: "Patria Investimentos",
      logo: "/images/Logo Patria Investimentos.png",
    },
    {
      name: "Galapagos Capital",
      logo: "/images/Logo_Galapagos_Capital_Color (1).png",
    },
    {
      name: "Itaú",
      logo: "/images/Logo Itau.png",
    },
    {
      name: "Honey Island",
      logo: "/images/Logo Honey Island.png",
    },
    {
      name: "Oliveira Trust",
      logo: "/images/Logo OLIVEIRA TRUST.png",
    },
  ];

  return (
    <section className="bg-slate-50 py-8 sm:py-12 lg:py-16 border-y border-slate-200" aria-labelledby="socios-heading">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-6xl mx-auto">
          <motion.p
            className="text-xs text-slate-500 text-center mb-6 sm:mb-10 font-medium uppercase tracking-wider"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Sócios e Parceiros
          </motion.p>

          {/* Desktop: Grid de 5 colunas */}
          <motion.div
            className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8 items-center justify-items-center"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {socios.map((socio, index) => (
              <motion.div
                key={index}
                className={`flex items-center justify-center w-full grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300 ${
                  socio.name === "Itaú" ? "h-16" : "h-12"
                }`}
                variants={fadeInUp}
              >
                <img
                  src={socio.logo}
                  alt={`Logo ${socio.name}`}
                  className={`w-auto object-contain ${
                    socio.name === "Honey Island"
                      ? "brightness-90 contrast-110 max-h-10"
                      : socio.name === "Itaú"
                      ? "max-h-16"
                      : "max-h-10"
                  }`}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Mobile: Layout em 2 linhas (3 + 2 centralizados) */}
          <motion.div
            className="sm:hidden"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Primeira linha: 3 logos */}
            <div className="flex items-center justify-center gap-6 mb-6">
              {socios.slice(0, 3).map((socio, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center justify-center grayscale opacity-70 ${
                    socio.name === "Itaú" ? "h-10 w-12" : "h-6 w-20"
                  }`}
                  variants={fadeInUp}
                >
                  <img
                    src={socio.logo}
                    alt={`Logo ${socio.name}`}
                    className={`w-auto object-contain ${
                      socio.name === "Itaú"
                        ? "max-h-10"
                        : "max-h-5"
                    }`}
                  />
                </motion.div>
              ))}
            </div>
            {/* Segunda linha: 2 logos centralizados */}
            <div className="flex items-center justify-center gap-8">
              {socios.slice(3).map((socio, index) => (
                <motion.div
                  key={index + 3}
                  className={`flex items-center justify-center grayscale opacity-70 ${
                    socio.name === "Honey Island" ? "h-8 w-16" : "h-6 w-28"
                  }`}
                  variants={fadeInUp}
                >
                  <img
                    src={socio.logo}
                    alt={`Logo ${socio.name}`}
                    className={`w-auto object-contain ${
                      socio.name === "Honey Island"
                        ? "brightness-90 contrast-110 max-h-6"
                        : "max-h-5"
                    }`}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Dois Pilares Section - Design mais limpo
function PilaresSection() {
  return (
    <section id="pilares" className="relative py-12 sm:py-20 lg:py-24" style={{ backgroundColor: 'rgb(231, 237, 242)' }} aria-labelledby="pilares-heading">
      {/* Background Pattern */}
      <BRLDPatternMinimal variant="subtle" opacity={0.03} size={32} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div
          className="max-w-3xl mx-auto text-center mb-8 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2
            id="pilares-heading"
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-4 px-2"
          >
            Casos de uso: uma stablecoin em real, desenhada como infraestrutura financeira
          </h2>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Card 1 - Pilar Liquidação */}
          <motion.div
            className="bg-white rounded-xl p-8 border border-slate-200 hover:border-slate-300 transition-all duration-200 flex flex-col h-full"
            variants={fadeInUp}
          >
            <div className="flex-1">
              <div className="w-12 h-12 rounded-lg bg-[#0038B5]/10 flex items-center justify-center mb-4">
                <IconCoin className="w-6 h-6 gradient-icon" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4">
                Moeda de liquidação para operações financeiras em blockchain
              </h3>
              <p className="text-sm sm:text-base text-slate-600 mb-4 leading-relaxed">
                Leve o real para o trilho on-chain e execute liquidação, juros, amortizações e distribuições de forma automatizada, com rastreabilidade e trilha de auditoria nativa.
              </p>
              <p className="text-xs sm:text-sm font-medium text-slate-500">
                Para quem: securitizadoras, originadores, plataformas de distribuição, estruturas tokenizadas e operações com múltiplos investidores.
              </p>
            </div>
            <a
              href="#pilar-liquidacao"
              className="inline-flex items-center text-sm sm:text-base font-medium text-[#0038B5] hover:text-[#003865] transition-colors mt-6"
            >
              Ver casos de liquidação
              <IconArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 gradient-icon" strokeWidth={2} />
            </a>
          </motion.div>

          {/* Card 2 - Pilar Tesouraria */}
          <motion.div
            className="bg-white rounded-xl p-8 border border-slate-200 hover:border-slate-300 transition-all duration-200 flex flex-col h-full"
            variants={fadeInUp}
          >
            <div className="flex-1">
              <div className="w-12 h-12 rounded-lg bg-[#0038B5]/10 flex items-center justify-center mb-4">
                <IconWallet className="w-6 h-6 gradient-icon" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4">
                Tesouraria corporativa para o caixa que não pode ficar parado
              </h3>
              <p className="text-sm sm:text-base text-slate-600 mb-4 leading-relaxed">
                Transforme o caixa corporativo em uma operação 24/7 com mais visibilidade e previsibilidade e menos dependente de janelas bancárias.
              </p>
              <p className="text-xs sm:text-sm font-medium text-slate-500">
                Para quem: empresas com caixa ocioso, tesourarias operacionais, fintechs, marketplaces e PSPs que precisam de gestão contínua de saldo.
              </p>
            </div>
            <a
              href="#pilar-tesouraria"
              className="inline-flex items-center text-sm sm:text-base font-medium text-[#0038B5] hover:text-[#003865] transition-colors mt-6"
            >
              Ver casos de tesouraria
              <IconArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 gradient-icon" strokeWidth={2} />
            </a>
          </motion.div>
        </motion.div>

        <p className="text-xs sm:text-sm text-slate-500 text-center mt-6 sm:mt-8 max-w-3xl mx-auto px-2">
          A BRLD é uma stablecoin institucional, pensada para infraestrutura de liquidação e tesouraria.
        </p>
      </div>
    </section>
  );
}

// O que é BRLD Section (Dobra 3)
function OQueEBRLDSection() {
  const iconFeatures = [
    {
      icon: IconTrendingUp,
      title: "Paridade",
      description: "1 BRLD = R$ 1"
    },
    {
      icon: IconShield,
      title: "Lastro integral",
      description: "Cada token emitido corresponde a R$ 1 em reserva mantida de forma segregada"
    },
    {
      icon: IconBolt,
      title: "Regras programáveis",
      description: "Via smart contracts, o dinheiro pode seguir trilhos (uso, destino e eventos)"
    }
  ];

  return (
    <section id="o-que-e" className="relative bg-slate-50 py-12 sm:py-20 lg:py-24" aria-labelledby="o-que-e-heading">
      {/* Background Pattern */}
      <BRLDPattern variant="subtle" opacity={0.03} size={24} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            id="o-que-e-heading"
            className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-6 sm:mb-12 text-left"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4 }}
          >
            O que é a BRLD?
          </motion.h2>

          <div className="grid lg:grid-cols-[3fr_2fr] gap-8 lg:gap-16 items-start">
            {/* Left Column - Text */}
            <motion.div
              className="text-sm sm:text-base lg:text-lg text-slate-700 leading-relaxed space-y-4 sm:space-y-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4 }}
            >
              <p>
                A BRLD é um token digital pareado 1:1 ao real brasileiro, lastreado integralmente em títulos públicos federais (Tesouro Nacional) e estruturado para operar com padrão institucional, governança, compliance e transparência.
              </p>
              <p>
                Na prática, ela permite que o real "caminhe" pela blockchain, sendo transferido, registrado e executado dentro de trilhas on-chain, com regras aplicadas por smart contracts.
              </p>
              <p>
                Isso conecta o mundo financeiro tradicional a estruturas digitais de forma segura, auditável e programável.
              </p>
              <p className="font-semibold text-slate-900">
                A BRLD não é cripto especulativo. É infraestrutura.
              </p>
            </motion.div>

            {/* Right Column - Icons */}
            <motion.div
              className="space-y-6 sm:space-y-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {iconFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 sm:gap-4"
                    variants={fadeInUp}
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#0038B5]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 gradient-icon" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Proposta de Valor Section (Dobra 4)
function PropostaValorSection() {
  const beneficios = [
    {
      icon: IconBolt,
      title: "Liquidação T+0 (quando o fluxo exige)",
      description: "Execute liquidações, eventos financeiros e repasses com lógica programável, no tempo do contrato.",
    },
    {
      icon: IconClock,
      title: "Operação 24/7/365",
      description: "Reduza a dependência de janelas bancárias, prazos e horários operacionais tradicionais.",
    },
    {
      icon: IconFileText,
      title: 'Dinheiro "carimbado" (regras embutidas)',
      description: "O valor segue o contrato automaticamente: destino, elegibilidade, prioridades e exceções.",
    },
    {
      icon: IconEye,
      title: "Rastreabilidade e auditoria nativas",
      description: "Trilha completa do fluxo financeiro, com verificabilidade on-chain em tempo real.",
    },
    {
      icon: IconShield,
      title: "Transparência de reservas",
      description: "Visibilidade contínua sobre lastro e tokens em circulação, com foco em governança.",
    },
    {
      icon: IconTrendingUp,
      title: "Stack nativa Liqi (integração com o TIDC)",
      description: "A BRLD atua como motor de liquidação de operações financeiras tokenizadas.",
    },
  ];

  return (
    <section className="relative py-12 sm:py-16 lg:py-20" style={{ background: '#0038B5' }} aria-labelledby="proposta-valor-heading">
      {/* Background Pattern - versão dark para fundo azul */}
      <BRLDPattern variant="dark" opacity={0.1} size={24} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-3xl mx-auto text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.p
            className="text-xs text-white mb-3 sm:mb-4 font-medium uppercase tracking-wider"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            Por que a BRLD?
          </motion.p>
          <h2
            id="proposta-valor-heading"
            className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white mb-4"
          >
            Eficiência operacional com padrão institucional
          </h2>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto mb-8 sm:mb-12"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {beneficios.map((beneficio, index) => {
            const IconComponent = beneficio.icon;
            return (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-md hover:shadow-lg transition-all duration-200 border border-white/20 hover:bg-white/15"
                variants={fadeInUp}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white mb-3 sm:mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-white mb-2 text-xs sm:text-sm">{beneficio.title}</h3>
                <p className="text-xs text-white/90 leading-relaxed">{beneficio.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="text-center">
          <a
            href="/login"
            className="inline-flex items-center justify-center rounded-lg px-6 sm:px-8 py-3 sm:py-3.5 text-sm font-medium bg-white text-[#0038B5] transition-all duration-200 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white"
          >
            Acessar a Plataforma BRLD
          </a>
          <p className="text-xs text-white/90 mt-3 sm:mt-4 px-4">
            Sem promessas vagas: arquitetura, lastro e governança foram desenhados para suportar o uso institucional.
          </p>
        </div>
      </div>
    </section>
  );
}

// Casos de Uso Unificados Section (com abas)
function CasosUsoSection() {
  const [activeTab, setActiveTab] = useState<"liquidacao" | "tesouraria">("tesouraria");

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === "#pilar-tesouraria") {
        setActiveTab("tesouraria");
        setTimeout(() => {
          const element = document.getElementById("casos-uso");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      } else if (hash === "#pilar-liquidacao") {
        setActiveTab("liquidacao");
        setTimeout(() => {
          const element = document.getElementById("casos-uso");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const casosUso = [
    "Pagamento de juros periódicos e eventos de amortização",
    "Waterfall de distribuição (repasses conforme regras pré-definidas)",
    "Distribuição para múltiplos investidores (sem fricção operacional)",
    "Recompra por inadimplência e eventos de exceção com lógica programada",
    "Validação de elegibilidade e cessão com trilha de auditoria",
  ];

  const dores = [
    "Caixa parado por fricção operacional entre resgate e reaplicação",
    "Saldos fragmentados, com baixa visão consolidada e controle do caixa",
    "Conciliações manuais e atraso no fechamento real da posição de caixa",
  ];

  const solucoes = [
    "Caixa consolidado em uma infraestrutura única, reduzindo fricção",
    "Liquidez diária, compatível com caixa operacional e planejamento",
    "Automação e rastreabilidade do fluxo, com visibilidade contínua",
  ];

  return (
    <section
      id="casos-uso"
      className="py-12 sm:py-16 lg:py-20 relative bg-white"
      aria-labelledby="casos-uso-heading"
    >
      <div id="pilar-liquidacao" className="absolute -mt-20" />
      <div id="pilar-tesouraria" className="absolute -mt-20" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="mb-6 sm:mb-12"
          >
            <p className="text-xs text-gray-500 mb-3 sm:mb-4 font-medium uppercase tracking-wider">
              Casos de Uso
            </p>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-semibold text-slate-900 text-left">
              Como a BRLD transforma operações financeiras
            </h2>
          </motion.div>

          {/* Tabs Modernas com Underline e CTA na mesma linha */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-16 pb-4 border-b border-slate-200 gap-4 sm:gap-0">
            <div className="relative inline-flex gap-4 sm:gap-8 -mb-px overflow-x-auto">
              <motion.button
                onClick={() => setActiveTab("liquidacao")}
                className={`relative pb-3 sm:pb-4 px-1 font-semibold text-sm sm:text-base transition-colors duration-200 whitespace-nowrap ${
                  activeTab === "liquidacao"
                    ? "text-[#0038B5]"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                whileTap={{ scale: 0.98 }}
              >
                Moeda de liquidação
                {activeTab === "liquidacao" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ backgroundColor: '#0038B5' }}
                    layoutId="activeTab"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
              <motion.button
                onClick={() => setActiveTab("tesouraria")}
                className={`relative pb-3 sm:pb-4 px-1 font-semibold text-sm sm:text-base transition-colors duration-200 whitespace-nowrap ${
                  activeTab === "tesouraria"
                    ? "text-[#0038B5]"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                whileTap={{ scale: 0.98 }}
              >
                Tesouraria corporativa
                {activeTab === "tesouraria" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ backgroundColor: '#0038B5' }}
                    layoutId="activeTab"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            </div>
          </div>

          {/* Conteúdo da aba Liquidação */}
          <motion.div
            key="liquidacao"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: activeTab === "liquidacao" ? 1 : 0, y: activeTab === "liquidacao" ? 0 : 20 }}
            transition={{ duration: 0.3 }}
            style={{ display: activeTab === "liquidacao" ? "block" : "none" }}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-slate-200 overflow-hidden relative z-10">
              <div className="p-4 sm:p-8 lg:p-10">
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-3 sm:mb-4">
                    BRLD como moeda de liquidação para operações tokenizadas
                  </h3>
                  <div className="text-sm sm:text-base text-slate-600 leading-relaxed space-y-2 sm:space-y-3">
                    <p>
                      Em estruturas de mercado de capitais, o desafio não é só "pagar". É liquidar corretamente: para as partes certas, na ordem prevista, no tempo do contrato, com regras claras e trilha de auditoria.
                    </p>
                    <p>
                      A BRLD leva o real para o trilho on-chain, permitindo que eventos financeiros sejam executados de forma programável, rastreável e verificável.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-slate-200">
                  <h4 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-base sm:text-lg">Casos de uso:</h4>
                  <ul className="space-y-2 sm:space-y-3">
                    {casosUso.map((caso, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start gap-2 sm:gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#0038B5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <IconCheck className="w-3 h-3 sm:w-4 sm:h-4 gradient-icon" strokeWidth={2} />
                        </div>
                        <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">{caso}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Comparação Visual */}
                <div>
                  <h4 className="text-left text-base sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-8">Comparação</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {/* Coluna Esquerda - Tradicional */}
                    <motion.div
                      className="relative"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="bg-gradient-to-br from-slate-50/80 to-slate-100/50 border border-slate-200/80 rounded-lg sm:rounded-xl p-4 sm:p-8 h-full relative z-10 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center justify-between mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-slate-200/60">
                          <h4 className="text-sm sm:text-xl font-semibold text-slate-800">No modelo tradicional:</h4>
                          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-slate-400"></div>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                          Regras ficam em planilhas, conciliações e processos manuais. A execução depende de janelas e checagens.
                        </p>
                      </div>
                    </motion.div>

                    {/* Coluna Direita - BRLD */}
                    <motion.div
                      className="relative"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="bg-gradient-to-br from-[#0038B5]/5 via-[#0038B5]/8 to-[#0038B5]/10 border border-[#0038B5]/20 rounded-lg sm:rounded-xl p-4 sm:p-8 h-full relative overflow-hidden z-10 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-[#0038B5]/10 to-[#0038B5]/5 rounded-full -mr-8 -mt-8 sm:-mr-12 sm:-mt-12"></div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-[#0038B5]/20">
                            <h4 className="text-sm sm:text-xl font-semibold text-slate-800">Com BRLD:</h4>
                            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#0038B5]"></div>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                            O dinheiro carrega as regras. A execução é automatizada, com trilha verificável e menor risco operacional.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                  <div className="pt-4 sm:pt-6 border-t border-slate-200 mt-6 sm:mt-8">
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      A BRLD transforma o real em um ativo programável, capaz de executar regras contratuais diretamente no trilho de liquidação.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Conteúdo da aba Tesouraria */}
          <motion.div
            key="tesouraria"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: activeTab === "tesouraria" ? 1 : 0, y: activeTab === "tesouraria" ? 0 : 20 }}
            transition={{ duration: 0.3 }}
            style={{ display: activeTab === "tesouraria" ? "block" : "none" }}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="p-4 sm:p-8 lg:p-10">
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-3 sm:mb-4">
                    Tesouraria corporativa para o caixa que exige liquidez e controle
                  </h3>
                  <div className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    <p>
                      Tesourarias convivem com caixa ocioso, gestão fragmentada em múltiplos bancos, conciliações demoradas e baixa visibilidade. A BRLD foi desenhada para consolidar e automatizar a operação de caixa com lógica 24/7, permitindo que recursos de curto prazo operem com eficiência e rendimento alinhado ao CDI.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <h4 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-base sm:text-lg">Dores típicas:</h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {dores.map((dor, index) => (
                        <motion.li
                          key={index}
                          className="flex items-start gap-2 sm:gap-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.3 }}
                        >
                          <div className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 sm:mt-2 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">{dor}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#0038B5]/5 border border-[#0038B5]/20 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <h4 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-base sm:text-lg">Com BRLD:</h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {solucoes.map((solucao, index) => (
                        <motion.li
                          key={index}
                          className="flex items-start gap-2 sm:gap-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.3 }}
                        >
                          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#0038B5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <IconCheck className="w-3 h-3 sm:w-4 sm:h-4 gradient-icon" strokeWidth={2} />
                          </div>
                          <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">{solucao}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h4 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-base sm:text-lg">Como funciona na prática</h4>
                  <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full gradient-icon-bg-subtle flex items-center justify-center flex-shrink-0">
                        <span className="gradient-icon font-semibold text-xs sm:text-sm">1</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Entrada</h5>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">A empresa aporta reais por canais autorizados, após validações operacionais e de compliance.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full gradient-icon-bg-subtle flex items-center justify-center flex-shrink-0">
                        <span className="gradient-icon font-semibold text-xs">2</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Uso</h5>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">A BRLD funciona como caixa digital, com rastreio e regras de uso conforme a necessidade da tesouraria.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full gradient-icon-bg-subtle flex items-center justify-center flex-shrink-0">
                        <span className="gradient-icon font-semibold text-xs">3</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Saída</h5>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">O resgate é solicitado a qualquer momento, com queima do token e liquidação em reais via trilhos locais.</p>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 sm:pt-6 border-t border-slate-200">
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      A BRLD permite centralizar o caixa corporativo em uma infraestrutura única, com liquidez contínua, automação de fluxos e visibilidade em tempo real, reduzindo fricções típicas da gestão tradicional.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Confiança Section (Dobra 7)
function ConfiancaSection() {
  const segurancaItems = [
    {
      icon: IconShield,
      title: "Lastro integral (100%)",
      items: [
        "Reserva composta por ativos públicos brasileiros de alta liquidez (Tesouro Nacional)",
        "Emissão só ocorre após alocação correspondente no lastro",
        "Queima do token no resgate"
      ]
    },
    {
      icon: IconLock,
      title: "Segregação de riscos",
      items: [
        { strong: "Fundação de governança:", text: " supervisiona regras e integridade no longo prazo" },
        { strong: "Custódia do lastro:", text: " reservas segregadas, liquidez para resgates, auditorias e controles independentes" },
        { strong: "Emissora e interface:", text: " emissão/queima, on/off-ramp em reais, KYC/AML e relacionamento" }
      ]
    },
    {
      icon: IconEye,
      title: "Transparência e auditabilidade",
      items: [
        "Relatórios periódicos de reservas",
        "Divulgação de tokens em circulação",
        "Verificabilidade associada à infraestrutura blockchain (auditabilidade)"
      ]
    }
  ];

  return (
    <>
      {/* Seção de Segurança - 3 cards estilo Framer */}
      <section id="seguranca" className="relative bg-white py-12 sm:py-16 lg:py-20" aria-labelledby="confianca-heading">
      {/* Background Pattern */}
      <BRLDPattern variant="subtle" opacity={0.03} size={24} />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              id="confianca-heading"
              className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-6 sm:mb-12 text-center px-2"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Segurança e conformidade: o que sustenta a BRLD
            </motion.h2>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {segurancaItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    className="bg-gradient-to-br from-slate-50 to-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 hover:border-slate-200 group"
                    variants={fadeInUp}
                    whileHover={{ y: -4, scale: 1.02 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#0038B5]/10 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 gradient-icon" strokeWidth={1.5} />
                      </div>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-base sm:text-xl">{item.title}</h3>
                    <ul className="space-y-2 sm:space-y-3">
                      {item.items.map((listItem, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 sm:gap-3">
                          <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 gradient-icon flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="text-xs sm:text-base text-slate-700 leading-relaxed">
                            {typeof listItem === 'string' ? listItem : (
                              <>
                                <strong>{listItem.strong}</strong>{listItem.text}
                              </>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Auditoria Fact Finance */}
      <section className="py-10 sm:py-12 lg:py-16" style={{ background: '#0038B5' }} aria-labelledby="fact-finance-heading">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-5 gap-6 sm:gap-8 items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              {/* Imagem */}
              <div className="md:col-span-2">
                <div className="relative h-48 sm:h-64 rounded-lg overflow-hidden bg-slate-900">
                  <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6">
                    <img
                      src="/images/fact_finance_oracle_cover.png"
                      alt="Fact Finance"
                      className="w-auto h-auto max-w-[180px] sm:max-w-[240px] brightness-0 invert"
                    />
                  </div>
                </div>
              </div>

              {/* Texto */}
              <div className="md:col-span-3">
                <h3
                  id="fact-finance-heading"
                  className="font-semibold text-white mb-4 sm:mb-6 text-lg sm:text-xl lg:text-2xl"
                >
                  Auditoria Fact Finance
                </h3>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <IconCheck className="w-4 h-4 text-white flex-shrink-0 mt-0.5 sm:mt-1" strokeWidth={2} />
                    <span className="text-xs sm:text-sm text-slate-200">
                      <strong className="text-white">Prova de Autenticidade:</strong> validação onchain da carteira garantindo dados diretos do provedor oficial.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <IconCheck className="w-4 h-4 text-white flex-shrink-0 mt-0.5 sm:mt-1" strokeWidth={2} />
                    <span className="text-xs sm:text-sm text-slate-200">
                      <strong className="text-white">Índice de Confiança:</strong> monitoramento de anomalias com técnicas estatísticas e detecção baseada em densidade.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <IconCheck className="w-4 h-4 text-white flex-shrink-0 mt-0.5 sm:mt-1" strokeWidth={2} />
                    <span className="text-xs sm:text-sm text-slate-200">
                      <strong className="text-white">Auditores Externos:</strong> grupo de auditores independentes valida a integridade e precisão dos dados.
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

// Componente para contador animado com formatação brasileira (exportado para uso futuro)
export function AnimatedCounter({ 
  value, 
  duration = 2.5,
  decimals = 2,
  hasDecimals = false
}: { 
  value: string; // Formato: "50.000.000,00" ou "1.572" ou "85"
  duration?: number;
  decimals?: number;
  hasDecimals?: boolean;
}) {
  const [displayValue, setDisplayValue] = useState("0");
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const elementRef = useRef<HTMLSpanElement>(null);

  // Converter string formatada para número
  const parseValue = (val: string): number => {
    // Remove pontos e substitui vírgula por ponto
    const cleaned = val.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  // Formatar número para formato brasileiro
  const formatToBrazilian = (num: number): string => {
    if (hasDecimals) {
      // Formato com decimais: 50.000.000,00
      const parts = num.toFixed(decimals).split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      
      // Adiciona pontos a cada 3 dígitos
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      
      return `${formattedInteger},${decimalPart}`;
    } else {
      // Formato sem decimais: 1.572 ou 85
      const integerPart = Math.floor(num).toString();
      return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };

  // Intersection Observer para detectar quando entra na viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [isVisible]);

  // Animação do contador
  useEffect(() => {
    if (!isVisible) return;
    
    const targetValue = parseValue(value);
    
    const startAnimation = () => {
      startTimeRef.current = Date.now();
      
      const animate = () => {
        if (!startTimeRef.current) return;
        
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        countRef.current = targetValue * easeOut;
        
        setDisplayValue(formatToBrazilian(countRef.current));
        
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
        }
      };
      
      animate();
    };
    
    // Delay pequeno para iniciar a animação
    const timeout = setTimeout(startAnimation, 200);
    
    return () => {
      clearTimeout(timeout);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible, value, duration, hasDecimals, decimals]);

  return <span ref={elementRef}>{displayValue}</span>;
}

// Auditoria On-chain Section
function AuditoriaOnChainSection() {
  const [copied, setCopied] = useState(false);
  const [showContractTooltip, setShowContractTooltip] = useState(false);
  const contractAddress = "0xfb67c0ca9366e5ae08ffed2f00de59d7e0537dfb";
  const explorerUrl = "https://xdcscan.com/token/0xfb67c0ca9366e5ae08ffed2f00de59d7e0537dfb";

  const handleCopy = () => {
    navigator.clipboard.writeText(contractAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="bg-white py-12 sm:py-16 lg:py-20" aria-labelledby="auditoria-onchain-heading">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h2
              id="auditoria-onchain-heading"
              className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-3 sm:mb-4 px-2"
            >
              Auditoria On-chain: Transparência em tempo real
            </h2>
            <motion.p
              className="text-sm sm:text-base text-slate-600 max-w-3xl mx-auto px-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Dados públicos e verificáveis, extraídos diretamente da blockchain. A confiança na BRLD começa com a transparência que qualquer pessoa pode auditar.
            </motion.p>
          </motion.div>

          {/* Bloco de Identidade do Token */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-slate-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Coluna Esquerda */}
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Nome do Token</p>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Brazilian Real Digital</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Rede Suportada</p>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">XDC Network</p>
                </div>
              </div>

              {/* Coluna Direita */}
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Símbolo</p>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">BRLD</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs text-slate-500">Endereço do Contrato</p>
                    <div className="relative">
                      <button
                        onMouseEnter={() => setShowContractTooltip(true)}
                        onMouseLeave={() => setShowContractTooltip(false)}
                        onClick={() => setShowContractTooltip(!showContractTooltip)}
                        className="text-amber-600 hover:text-amber-700 transition-colors"
                        aria-label="Informação sobre segurança do endereço"
                      >
                        <IconAlertCircle className="w-4 h-4" strokeWidth={2} />
                      </button>
                      {showContractTooltip && (
                        <div className="absolute left-0 sm:left-auto sm:right-0 bottom-full mb-2 w-56 sm:w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg z-10">
                          <p>Atenção: Para evitar fraudes, sempre confirme se este é o endereço oficial do contrato BRLD antes de interagir.</p>
                          <div className="absolute left-4 sm:left-auto sm:right-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={contractAddress}
                        className="flex-1 text-[10px] sm:text-xs font-mono text-slate-700 bg-white px-2 sm:px-3 py-2 rounded border border-slate-300 w-full"
                      />
                      <button
                        onClick={handleCopy}
                        className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors whitespace-nowrap"
                      >
                        {copied ? (
                          <>
                            <IconCircleCheck className="w-4 h-4" strokeWidth={2} />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <IconCopy className="w-4 h-4" strokeWidth={2} />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                    >
                      <IconExternalLink className="w-4 h-4" strokeWidth={2} />
                      Ver no Explorer
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Linha de Atualização e Fonte */}
          <div className="text-center px-2">
            <p className="text-[10px] sm:text-xs text-slate-500">
              Última atualização: 20/01/2026, 14:35:12 (GMT-3). Dados extraídos diretamente do explorador de blocos da XDC Network. A Liqi não controla ou altera estes dados.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Diferenciais Liqi Section (Dobra 9)
function DiferenciaisLiqiSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      className="py-12 sm:py-16 lg:py-20"
      style={{ backgroundColor: '#212121' }}
      aria-labelledby="diferenciais-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            id="diferenciais-heading"
            className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white mb-8 sm:mb-12 text-center"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            Tração da infraestrutura Liqi
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-12"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div
              className="text-center"
              variants={fadeInUp}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 flex items-baseline justify-center gap-1">
                <span className="text-[10px] sm:text-xs" style={{ fontSize: '0.3em' }}>R$</span>
                {isVisible ? (
                  <AnimatedNumber value={1000000000} suffix="+" duration={2} format="billion" />
                ) : (
                  <span>0B+</span>
                )}
              </div>
              <div className="text-xs sm:text-sm text-white/80">em operações via TIDC</div>
            </motion.div>
            <motion.div
              className="text-center"
              variants={fadeInUp}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">
                {isVisible ? (
                  <AnimatedNumber value={7000000} suffix="+" duration={2} format="million" />
                ) : (
                  <span>0M+</span>
                )}
              </div>
              <div className="text-xs sm:text-sm text-white/80">recebíveis geridos em blockchain</div>
            </motion.div>
            <motion.div
              className="text-center"
              variants={fadeInUp}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">
                {isVisible ? (
                  <AnimatedNumber value={75} suffix="+" duration={1.5} format="plain" />
                ) : (
                  <span>0+</span>
                )}
              </div>
              <div className="text-xs sm:text-sm text-white/80">empresas parceiras usando a BRLD</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// FAQ Section (Dobra 10)
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "A BRLD é pareada como?",
      answer: "A BRLD mantém paridade 1:1 com o real brasileiro. Para cada token BRLD em circulação, existe R$ 1,00 equivalente em reserva. Essa paridade é garantida pela estrutura de lastro e verificável em tempo real através da auditoria on-chain.",
    },
    {
      question: "Qual é o lastro?",
      answer: "O lastro é composto 100% por títulos públicos federais (Tesouro Direto), considerados os ativos de menor risco do mercado brasileiro. Os títulos são mantidos em estrutura segregada, custodiados por instituição financeira regulada, garantindo que o patrimônio dos detentores de BRLD esteja sempre protegido e separado de qualquer outro ativo.",
    },
    {
      question: "BRLD é para varejo?",
      answer: "O foco inicial da BRLD é uso institucional: empresas que precisam de liquidação eficiente em operações tokenizadas e corporações buscando modernizar sua tesouraria. Não é uma stablecoin para trading especulativo ou uso em exchanges de varejo. Futuramente, novos casos de uso podem ser explorados conforme a regulamentação evolui.",
    },
    {
      question: "Como a emissão acontece?",
      answer: "O processo de emissão segue um fluxo estruturado: a empresa realiza um aporte em reais via transferência bancária, passa por validações de compliance (KYC/AML), e após a confirmação do depósito, os tokens BRLD são emitidos na carteira da empresa. Simultaneamente, o valor correspondente é alocado em títulos do Tesouro, mantendo o lastro integral.",
    },
    {
      question: "Como funciona o resgate?",
      answer: "O resgate é simples e direto: a empresa solicita a conversão de BRLD para reais através da plataforma, os tokens são queimados (burn) de forma verificável na blockchain, e o valor em reais é transferido para a conta bancária cadastrada. O processo típico leva até D+1 útil, dependendo do horário da solicitação.",
    },
    {
      question: "Há transparência/auditoria?",
      answer: "Transparência é um pilar fundamental da BRLD. Oferecemos: auditoria on-chain em tempo real (qualquer pessoa pode verificar o supply e as transações), relatórios periódicos de reservas, auditoria independente pela Fact Finance, e total visibilidade do lastro em títulos públicos. A confiança é construída com dados verificáveis, não apenas promessas.",
    },
    {
      question: "Posso programar regras de uso?",
      answer: "Sim, essa é uma das principais vantagens da BRLD. Através de smart contracts, é possível criar 'dinheiro carimbado' com regras programáveis: definir destinatários elegíveis, estabelecer condições de uso, criar gatilhos automáticos baseados em eventos, e implementar políticas de compliance diretamente no fluxo financeiro. Ideal para pagamentos condicionados, incentivos e controle de despesas.",
    },
    {
      question: "Como começo?",
      answer: "O primeiro passo é acessar a Plataforma BRLD e iniciar o processo de habilitação. Nossa equipe irá conduzir o onboarding, que inclui: entendimento do seu caso de uso, validação de compliance, configuração da carteira empresarial e treinamento na plataforma. O processo é consultivo e personalizado para as necessidades da sua operação.",
    },
  ];

  return (
    <section id="faq" className="relative py-12 sm:py-16 lg:py-24" style={{ backgroundColor: '#242a32' }} aria-labelledby="faq-heading">
      {/* Background Pattern */}
      <BRLDPattern variant="dark" opacity={0.05} size={24} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Título à esquerda */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-4 sm:space-y-6"
            >
              <h2
                id="faq-heading"
                className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white leading-tight"
              >
                FAQ: o que times institucionais perguntam antes de avançar
              </h2>
              <ul className="space-y-2 sm:space-y-3 pt-2 sm:pt-4">
                <li className="flex items-start gap-2 sm:gap-3">
                  <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Paridade 1:1 garantida com lastro em títulos do Tesouro
                  </span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Transparência total com auditoria onchain
                  </span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Smart contracts para regras de uso programáveis
                  </span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Liquidação instantânea e eficiente
                  </span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <IconCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Foco em uso institucional e corporativo
                  </span>
                </li>
              </ul>
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-white hover:bg-white/10 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white mt-4 sm:mt-6"
              >
                Acessar plataforma
              </a>
            </motion.div>

            {/* Lista de perguntas à direita */}
            <div className="space-y-0">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className={`border-b last:border-b-0 transition-colors duration-200 ${
                    openIndex === index ? "border-[#F556E9]/30" : "border-white/10"
                  }`}
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className={`w-full py-5 sm:py-6 text-left flex items-center justify-between group transition-all duration-200 ${
                      openIndex === index 
                        ? "text-[#F556E9]" 
                        : "text-white hover:opacity-80"
                    }`}
                    aria-expanded={openIndex === index}
                  >
                    <span className={`text-base sm:text-lg font-medium pr-6 flex-1 ${
                      openIndex === index ? "text-[#F556E9]" : "text-white"
                    }`}>
                      {faq.question}
                    </span>
                    {openIndex === index ? (
                      <IconMinus
                        className="w-5 h-5 sm:w-6 sm:h-6 text-[#F556E9] flex-shrink-0 transition-all duration-300"
                        strokeWidth={2}
                      />
                    ) : (
                      <IconPlus
                        className="w-5 h-5 sm:w-6 sm:h-6 text-white flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                        strokeWidth={2}
                      />
                    )}
                  </button>
                  {openIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="text-sm sm:text-base text-white/80 leading-relaxed pb-5 sm:pb-6 pr-11">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// CTA Final Section
function CTAFinalSection() {
  return (
    <section className="relative py-12 sm:py-16 lg:py-20" style={{ background: '#0038B5' }} aria-labelledby="cta-final-heading">
      {/* Background Pattern */}
      <BRLDPattern variant="dark" opacity={0.1} size={24} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            id="cta-final-heading"
            className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-semibold text-white mb-4 sm:mb-6 px-2"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            Pronto para começar com a BRLD?
          </motion.h2>
          <motion.p
            className="text-sm sm:text-base lg:text-lg text-white/90 mb-6 sm:mb-8 leading-relaxed px-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            Acesse a Plataforma BRLD e descubra como a stablecoin institucional pode transformar sua operação de liquidação e tesouraria.
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="/login"
              className="inline-flex items-center justify-center rounded-lg px-6 sm:px-8 py-3 sm:py-3.5 text-sm font-medium bg-white text-[#0038B5] transition-all duration-200 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white"
            >
              Acessar a Plataforma BRLD
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* SVG Gradient Definition para ícones */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="gradient-blue-black" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0038B5" />
            <stop offset="100%" stopColor="#212121" />
          </linearGradient>
        </defs>
      </svg>
      
      <Header />
      <ScrollToTop />
      <CookieBanner />

      <main>
        <HeroSection />
        <FeaturesCarouselSection />
        <SociosSection />
        <PilaresSection />
        <OQueEBRLDSection />
        <PropostaValorSection />
        <DiferenciaisLiqiSection />
        <CasosUsoSection />
        <ConfiancaSection />
        <AuditoriaOnChainSection />
        <FAQSection />
        <CTAFinalSection />
      </main>

      <Footer />
    </div>
  );
}
